import React, { useContext, useEffect, useRef, useState } from 'react';
import { CompanyContext } from '../../../context/CompanyContext';
import { Navigate, Route, Routes } from 'react-router-dom';
import { CompanyRoleAssignmentContext } from '../../../context/CompanyRoleAssignmentContext';
import { UserContext } from '../../../context/UserContext';
import { CompanyInviteState } from '../../../types/company_role_assignment/companyRoleAssignment';
import AcceptCompanyInviteScreen from '../Authentication/AcceptCompanyInviteScreen';
import NotificationHandler from '../../../subscriptions/NotificationHandler';
import IntercomBadge from '../Support/IntercomBadge';
import { Routing } from '../../../util/routes.util';
import { OnboardingLS } from '../../../util/local_storage/onboarding.ls';
import OnboardingCompleteModal from '../Onboarding/modals/OnboardingCompleteModal';
import PromptUserRenameModal from './Modals/PromptUserRenameModal';
import { MainSidebar } from '../../components/Sidebar';
import AlertScreen from '../Alerts/AlertScreen';
import AssetScreen from '../Asset/AssetScreen';
import DashboardScreen from '../Dashboard/DashboardScreen';
import OperationScreen from '../Operation/OperationScreen';
import ReferenceDataScreen from '../ReferenceData/ReferenceDataScreen';
import SettingsScreen from '../Settings/SettingsScreen';
import StockScreen from '../Stock/StockScreen';
import TaskScreen from '../Task/TaskScreen';
import StockLocationRoleAssignmentProvider from '../../../providers/StockLocationRoleAssignmentProvider';
import { useCompanyApi } from './load';
import PartnerSettingsScreen from '../Settings/Partner/PartnerSettingsScreen';
import CreatePdfTemplateScreen from '../PdfTemplateEditor/CreatePdfTemplateScreen';
import UpdatePdfTemplateScreen from '../PdfTemplateEditor/UpdatePdfTemplateScreen';
import Provider from '../../../providers/common/Provider';
import BarcodeExportConfigurationLabelEditor from '../Settings/Barcodes/Label/BarcodeExportConfigurationLabelEditor';
import BarcodeExportConfigurationProvider from '../../../providers/BarcodeExportConfigurationProvider';

export default function CompanyScreen() {
  const { currentUser } = useContext(UserContext);
  if (!currentUser) return <Navigate to={Routing.authentication.login()} />;

  const { currentCompany } = useContext(CompanyContext);
  if (!currentCompany.id) return <Navigate to={Routing.companies.overview()} />;

  const { reloadCompany } = useCompanyApi(currentCompany);

  const executed = useRef(false);

  useEffect(() => {
    if (executed.current) return;
    executed.current = true;

    reloadCompany(currentCompany);
  }, []);

  const { companyRoles } = useContext(CompanyRoleAssignmentContext);
  const pendingCompanyInvites = (companyRoles.get(currentUser.userId) || []).filter(
    cr => cr.state === CompanyInviteState.pending,
  );
  if (pendingCompanyInvites.length) {
    return <AcceptCompanyInviteScreen role={pendingCompanyInvites[0]} />;
  }

  const [openUserRenamePrompt, setOpenUserRenamePrompt] = useState<boolean>(false);
  const [onboardingCompletModalOpen, setOnboardingCompleteModalOpen] = useState(false);

  if (
    currentUser?.firstName === 'Invited' &&
    currentUser?.lastName &&
    !openUserRenamePrompt &&
    process.env.REACT_APP_ENV !== 'test'
  ) {
    setOpenUserRenamePrompt(true);
  }

  useEffect(() => {
    const showOnboardingSummary = OnboardingLS.getOnboardedState();
    if (showOnboardingSummary) {
      setOnboardingCompleteModalOpen(true);
    }
  }, []);

  useEffect(() => {
    if (onboardingCompletModalOpen) OnboardingLS.removeOnboardedState();
  }, [onboardingCompletModalOpen]);

  return (
    <>
      <OnboardingCompleteModal open={onboardingCompletModalOpen} setOpen={setOnboardingCompleteModalOpen} />
      {/* TODO: MOVE */}
      <PromptUserRenameModal open={openUserRenamePrompt} setOpen={v => setOpenUserRenamePrompt(v)} />

      <IntercomBadge />
      <NotificationHandler />
      <Routes>
        <Route path='/settings/*' element={<SettingsScreen />} />
        <Route
          path='/pdf_template_editor/create/:entityType'
          element={
            <Provider pdfTemplate>
              <CreatePdfTemplateScreen />
            </Provider>
          }
        />
        <Route
          path='/pdf_template_editor/update/:id'
          element={
            <Provider pdfTemplate>
              <UpdatePdfTemplateScreen />
            </Provider>
          }
        />
        <Route
          path='/barcode_label_editor/update/:id'
          element={
            <BarcodeExportConfigurationProvider>
              <BarcodeExportConfigurationLabelEditor />
            </BarcodeExportConfigurationProvider>
          }
        />

        <Route
          path='/*'
          element={
            <div className='flex flex-grow overflow-hidden'>
              <StockLocationRoleAssignmentProvider>
                <MainSidebar />
              </StockLocationRoleAssignmentProvider>

              <div
                className='flex flex-grow flex-col overflow-x-hidden'
                style={{ backgroundColor: 'white' || '#f5f6f7' }}
              >
                <Routes>
                  <Route path='/*' element={<Navigate to={'/dashboard'} />} />

                  <Route path='/dashboard' element={<DashboardScreen />} />
                  <Route path='/stock/*' element={<StockScreen />} />
                  <Route path='/reference_data/*' element={<ReferenceDataScreen />} />
                  <Route path='/tasks/*' element={<TaskScreen />} />
                  <Route path='/operations/*' element={<OperationScreen />} />
                  <Route path='/alerts/*' element={<AlertScreen />} />
                  <Route path='/assets/*' element={<AssetScreen />} />
                  <Route path='/partner/*' element={<PartnerSettingsScreen />} />
                </Routes>
              </div>
            </div>
          }
        />
      </Routes>
    </>
  );
}
